/**
 * Input Detection Layer
 *
 * Determines the type of input provided by the user:
 * - JSON configuration (wrapped, single-server, multi-server)
 * - CLI command (claude, gemini, codex)
 * - Unknown format
 *
 * This allows the converter to route to the appropriate parser.
 */

/**
 * Types of input we can handle
 */
export type InputType = 'json' | 'cli-command' | 'unknown';

/**
 * CLI tool types we support
 */
export type CliToolType = 'claude' | 'gemini' | 'codex';

/**
 * Result of input type detection
 */
export interface InputDetectionResult {
  /** The general type of input */
  type: InputType;
  /** For CLI commands, which tool's syntax */
  cliTool?: CliToolType;
  /** Cleaned/normalized input string */
  normalized: string;
  /** Confidence level for the detection */
  confidence: 'high' | 'medium' | 'low';
}

/**
 * CLI command patterns for detection
 */
const CLI_PATTERNS: Array<{
  tool: CliToolType;
  patterns: RegExp[];
}> = [
  {
    tool: 'claude',
    patterns: [/^claude\s+mcp\s+add(-json)?\b/i, /^claude\s+mcp\s+add\s/i],
  },
  {
    tool: 'gemini',
    patterns: [/^gemini\s+mcp\s+add\b/i],
  },
  {
    tool: 'codex',
    patterns: [/^codex\s+mcp\s+add\b/i],
  },
];

/**
 * Detects the type of input provided by the user
 *
 * @param input - Raw input string from the user
 * @returns Detection result with type, tool (if CLI), and normalized input
 *
 * @example
 * // JSON input
 * detectInputType('{"mcpServers": {...}}')
 * // => { type: 'json', normalized: '...', confidence: 'high' }
 *
 * @example
 * // Claude CLI command
 * detectInputType('claude mcp add --transport http myserver https://example.com')
 * // => { type: 'cli-command', cliTool: 'claude', normalized: '...', confidence: 'high' }
 */
export const detectInputType = (input: string): InputDetectionResult => {
  const trimmed = input.trim();

  // Empty input
  if (!trimmed) {
    return { type: 'unknown', normalized: '', confidence: 'low' };
  }

  // Check for JSON (starts with { or [)
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    // Verify it's valid JSON-like by checking for basic structure
    const hasValidJsonStructure =
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'));

    return {
      type: 'json',
      normalized: trimmed,
      confidence: hasValidJsonStructure ? 'high' : 'medium',
    };
  }

  // Check for CLI commands
  const matchedCli = CLI_PATTERNS.find(({ patterns }) =>
    patterns.some((pattern) => pattern.test(trimmed)),
  );
  if (matchedCli) {
    return {
      type: 'cli-command',
      cliTool: matchedCli.tool,
      normalized: trimmed,
      confidence: 'high',
    };
  }

  // Check for partial/fuzzy matches (user might have typos)
  const lowerInput = trimmed.toLowerCase();
  if (lowerInput.includes('claude') && lowerInput.includes('mcp')) {
    return {
      type: 'cli-command',
      cliTool: 'claude',
      normalized: trimmed,
      confidence: 'low',
    };
  }
  if (lowerInput.includes('gemini') && lowerInput.includes('mcp')) {
    return {
      type: 'cli-command',
      cliTool: 'gemini',
      normalized: trimmed,
      confidence: 'low',
    };
  }
  if (lowerInput.includes('codex') && lowerInput.includes('mcp')) {
    return {
      type: 'cli-command',
      cliTool: 'codex',
      normalized: trimmed,
      confidence: 'low',
    };
  }

  return { type: 'unknown', normalized: trimmed, confidence: 'low' };
};

/**
 * Get a human-readable description of the detected input type
 */
export const getInputTypeDescription = (result: InputDetectionResult): string => {
  if (result.type === 'json') {
    return 'JSON configuration';
  }

  if (result.type === 'cli-command' && result.cliTool) {
    const toolNames: Record<CliToolType, string> = {
      claude: 'Claude Code',
      gemini: 'Gemini CLI',
      codex: 'Codex CLI',
    };
    return `${toolNames[result.cliTool]} command`;
  }

  return 'Unknown format';
};
